/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useMemo, useState } from 'react';
// utils
import { url as appUrl  } from 'utils/urls';
import { datetoAPIString, getEasternTimeDiff } from 'utils/date';

import { useSelector, useDispatch } from 'react-redux';
// material
import { SingleSelectDropDown, Tip, DateRangePickerDropDown, TextField, CheckboxField } from '@insticator/insticator-ui/dist/material';
import Space from 'material/space/space';
import { SectionGroupV2 } from 'material/expandablesection/v2'
import { ConfigToggle } from 'material/expandablesection/v2'
import PreviewConfig from 'material/previewconfig/previewconfig'
import ArticleCard from 'material/articlecard/articleccard'

// styles
import injectSheet from 'react-jss'
import styles from './featuredarticle_styles'
// utils
import {getDateNYC} from '@insticator/insticator-ui';
import { setTrendingNowConfigSectionValid } from 'state/slices/trendingnowv2';
// static

const formatTime = (num) => ('0' + Math.abs(num)).slice(-2)

const generateTimeSlots = () => {
  const slots = []
  for(let i = 0; i < 24; i++) {
    const isAM = i < 12 ? 'AM' : 'PM'
    const timeDiff = getEasternTimeDiff() // EST is the same as UTC -5, unless observing Daylight Saving Time, then it becomes UTC -4
    const displayTimeEST = formatTime(i === 0 ? 12 : (i < 13 ? i : i - 12))
    const display12HrESTTime = `${displayTimeEST}:00 ${isAM} EST`

    const timeEST = i >= 24 ? (i - 24) : i
    const formattedTimeEST = `${formatTime(timeEST)}:00:00.000-0${timeDiff}:00`
    slots.push({
      name: display12HrESTTime,
      value: formattedTimeEST
    })
  }
  return slots
}

const timeSlots = [...generateTimeSlots()]

const defaultStartTime = timeSlots[0].value
const defaultEndTime = timeSlots[23].value

const allDayStartTime = `00:00:00.000-0${getEasternTimeDiff()}:00`
const allDayEndTime = `23:59:59.999-0${getEasternTimeDiff()}:00`

const generateDefaultStartDate = () => {
  let week = new Date(getDateNYC());
  return datetoAPIString(week);
};

const generateDefaultEndDate = () => {
  let today = new Date(getDateNYC());
  today.setDate(today.getDate() + 7)
  return datetoAPIString(today);
};

const getDisabledDates = () => [{
  before: new Date(getDateNYC())
}]

/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: ADVANCED SECTION : FEATURED ARTICLE ~~~~~~~~~~ ========== */
const FeaturedArticle = injectSheet(styles)((props) => {

  const { enable, url, urlHash, setDuration, startDateEST, endDateEST, isSectionOpen, onChange } = props
  
  // State to reset date and time values
  const [isSameDayInit, setIsSameDayInit] = useState(false)
  const [isAllDayOptionInit, setIsAllDayOptionInit] = useState(false)

  const [isAllDay, setIsAllDay] = useState(false)

  const [startTime, setStartTime] = useState(defaultStartTime)
  const [endTime, setEndTime] = useState(defaultEndTime)

  const dispatch = useDispatch()

  const isArticleUrlValid = useMemo(() => {
    if(!enable) {
      dispatch(setTrendingNowConfigSectionValid({ configName: 'advancedConfig', isValid: true }))
      return true
    }
    if(!url) {
      dispatch(setTrendingNowConfigSectionValid({ configName: 'advancedConfig', isValid: false }))
      return false
    }
    try {
      new URL(url)
      dispatch(setTrendingNowConfigSectionValid({ configName: 'advancedConfig', isValid: true }))
      return true
    } catch (error) {
      dispatch(setTrendingNowConfigSectionValid({ configName: 'advancedConfig', isValid: false }))
      return false
    }
  }, [enable, url, dispatch])

  useEffect(() => {
    if(startDateEST  && typeof startDateEST === 'string') setStartTime(startDateEST.substring(11, 30))
    if(endDateEST && typeof endDateEST === 'string') setEndTime(endDateEST.substring(11, 30))
  }, [startDateEST, endDateEST])

  useEffect(() => {
    if(isAllDayOptionInit) return
    if(startTime === allDayStartTime && endTime === allDayEndTime) {
      setIsAllDay(true) 
      setIsAllDayOptionInit(true)
    }
  }, [startTime, endTime, isAllDayOptionInit])

  useEffect(() => {
    // Reset dates and times on section close
    if(!isSectionOpen) {
      setIsAllDayOptionInit(false)
      setIsSameDayInit(false)
    }
  }, [isSectionOpen])

  const handleFeaturedArticleChange = (newProperty) => {
    const currentProperties = {
      enable,
      url,
      urlHash,
      setDuration,
      startDateEST: startDateEST || `${selectedDateRange.from.substring(0,10)}T${startTime}`,
      endDateEST: endDateEST || `${selectedDateRange.to.substring(0,10)}T${endTime}`
    }
    onChange({ featuredArticleDetails: {...currentProperties, ...newProperty}})
  }

  const handleIsAllDayChange = (isAllDayChecked) => {
    let selectedStartTime = startTime
    let selectedEndTime = endTime
    if(isAllDayChecked) {
      selectedStartTime = allDayStartTime
      selectedEndTime = allDayEndTime
    } else {
      selectedStartTime = defaultStartTime
      selectedEndTime = defaultEndTime
    }
    handleFeaturedArticleChange({ startDateEST:`${selectedDateRange.from.substring(0,10)}T${selectedStartTime}`, endDateEST: `${selectedDateRange.to.substring(0,10)}T${selectedEndTime}` })
    setStartTime(selectedStartTime)
    setEndTime(selectedEndTime)
    setIsAllDay(isAllDayChecked)
  }

  const handleStartTimeChange = (time) => {
    handleFeaturedArticleChange({ startDateEST:`${selectedDateRange.from.substring(0,10)}T${time}`, endDateEST: `${selectedDateRange.to.substring(0,10)}T${endTime}` })
    setStartTime(time)
  }

  const handleEndTimeChange = (time) => {
    handleFeaturedArticleChange({ startDateEST:`${selectedDateRange.from.substring(0,10)}T${startTime}`, endDateEST: `${selectedDateRange.to.substring(0,10)}T${time}` })
    setEndTime(time)
  }

  const [selectedDateRange, selectDateRange] = useState({
    to: endDateEST && typeof endDateEST === 'string' ? endDateEST.substring(0,10) : generateDefaultEndDate(),
    from: startDateEST && typeof startDateEST === 'string' ? startDateEST.substring(0,10) : generateDefaultStartDate(),
    shortcut: ''
  })

  const isSameDay = selectedDateRange.from === selectedDateRange.to
  
  const endTimeSlots = isSameDay ? timeSlots.filter((timeSlot, index) => index > timeSlots.findIndex(time => time.value === startTime)) : timeSlots

  const startTimeSLots = isSameDay ? timeSlots.filter((timeSlot, index) => index < timeSlots.findIndex(time => time.value === endTime)) : timeSlots


  useEffect(() => {
    // when changing to same day, set a default start time and end time
    if(!isSameDayInit) return
    if(isSameDay) {
      setStartTime(defaultStartTime)
      setEndTime(defaultEndTime)
    }
  }, [isSameDay])

  const startTimeSlot = timeSlots.find(time => time.value === startTime)?.name || null

  const endTimeSlot = timeSlots.find(time => time.value === endTime)?.name || null

  return (
    <SectionGroupV2 
      indented={true} 
      klass={`featuredarticle ${props.classes.featuredarticle}`} 
      title={'Promoted article'}  // Rename "Featured" to "Promoted" article TREND2-135
      description={'Ability to highlight a specific article from your site to boost traffic.'}
      rowGap={32} 
      direction={'column'}>
        <Space gap={48}>
          <Space gap={16} direction={'column'}>
            <ConfigToggle 
              title={<span>
                Enable promoted article{' '}
                <Tip 
                  space={'none'} 
                  icon={'question-circle'} 
                  label={''} 
                  content={`Replaces the featured article with a monetized article to boost traffic`} 
                />
              </span>}
              checked={enable}
              onChange={val => handleFeaturedArticleChange({ enable: val})}
            >
            <Space direction={'column'} gap={4}>
              <TextField 
                type={`text`} 
                name={`articleURL`} 
                value={url} 
                handleChange={e => handleFeaturedArticleChange({'url': e.target.value})}
                placeholder={`Enter article url`} 
                valid={isArticleUrlValid}
                maxLength={500}
                required={enable}
              />
              {!isArticleUrlValid && <div className='errormessage'>Article URL is required and should be a valid url.</div>}
            </Space>
            <CheckboxField 
              type={`checkbox`} 
              name={`setduration`} 
              value={setDuration} 
              checked={setDuration} 
              valid
              label={<span>Set duration <Tip 
                space={'none'} 
                icon={'question-circle'} 
                label={''} 
                content={`If this option is unchecked, the duration is indefinite`} 
              /></span>} 
              handleChange={() => handleFeaturedArticleChange({ setDuration: !setDuration })} 
            />
            {setDuration && (
              <Space gap={16} direction={'column'}>
                <DateRangePickerDropDown
                  klass={`filter-date`}
                  indicatorTitle={'Date range'}
                  hideButtons={true}
                  showShortcuts={false}
                  showShortcutTextInPreview={false}
                  customDateRanges={false}
                  use4PMLogic={false}
                  range={selectedDateRange}
                  updateRange={
                    range => {
                      setIsSameDayInit(true)
                      handleFeaturedArticleChange({ startDateEST:`${range.from}T${startTime}`, endDateEST: `${range.to}T${endTime}` })
                      selectDateRange({...selectedDateRange, ...range})
                    }
                  }
                  disabledDays={getDisabledDates()}
                />
                <CheckboxField 
                  type={`checkbox`} 
                  name={`settime`} 
                  value={isAllDay} 
                  checked={isAllDay} 
                  valid
                  label={<span>All day</span>} 
                  handleChange={() => handleIsAllDayChange(!isAllDay)} 
                />
                {!isAllDay && (startTimeSlot && endTimeSlot) && (
                  <Space style={{height: '70px'}}>
                    <SingleSelectDropDown 
                      label={'Start Time'}
                      name={startTimeSlot} 
                      searchable={false} 
                      onSelect={opt => handleStartTimeChange(opt.value)}
                      items={startTimeSLots}
                    />
                    <SingleSelectDropDown 
                      label={'End Time'}
                      name={endTimeSlot} 
                      searchable={false} 
                      onSelect={opt => handleEndTimeChange(opt.value)}
                      items={endTimeSlots}
                    />
                </Space>
                )}
              </Space>
            )}
            </ConfigToggle>
          </Space>
          <PreviewConfig>
            <ArticleCard 
              promoted={enable}
              featured={!enable}
              title={'Sample article headline from your site'} 
              totalComments={200}
              direction={'horizontal'}
              imageSrc={`${appUrl.image}/2.0/trendingnow/article-image-1.jpg`}
            />
          </PreviewConfig>
        </Space>
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default FeaturedArticle;